/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Feature Override ViewModel — state for the DEBUG-ONLY Feature Override
 * screen, which configures two independent things per feature:
 *
 *   - **Stage** — evaluate the feature at a different lifecycle stage.
 *   - **Activation Override** — force it ON/OFF, skipping the whole decision.
 *
 * Persistence side effects go through `createAsyncThunk`s that call the
 * dev-tools `featureOverrideStorage` / `featureStageOverrideStorage`; reducers
 * only mutate UI state. A release build never installs the `123456` trigger and
 * `openFeatureOverride` guards on `isDevToolsBuild()`, so the screen can never
 * appear there.
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import { isDevToolsBuild } from "../../devtools/appVersionOverride";
import {
  clearAllFeatureOverrides,
  clearAllFeatureStageOverrides,
  clearFeatureOverride,
  clearFeatureStageOverride,
  readFeatureOverrides,
  readFeatureStageOverrides,
  setFeatureOverride,
  setFeatureStageOverride
} from "../../devtools/featureOverride";
import {
  featureRegistry,
  OVERRIDABLE_FEATURE_STAGES,
  registeredFeatures,
  resolveEffectiveStage
} from "../../model/featureActivation";
import type { FeatureStage } from "../../model/featureActivation";
import { Log } from "../../model/logging";
import {
  getControlCount,
  getResetActivationIndex,
  getResetStageIndex,
  getVideoDebugOverlayIndex,
  resolveControl
} from "./featureOverrideControls";
import { FeatureOverrideControlKind } from "./featureOverride.types";
import type {
  FeatureOverrideState,
  TFeatureOverrideControl,
  TFeatureOverrideCycleDirection,
  TFeatureOverrideRow
} from "./featureOverride.types";

const TAG: string = "featureOverrideSlice";

/**
 * Every thunk and selector below only ever reads `state.featureOverride`.
 *
 * The screen also hosts a Video Debug Overlay switch, but this slice
 * deliberately knows nothing about it beyond where it sits in the focus
 * order: its value belongs to `videoDebugOverlay`, and reaching across
 * would drag that slice's metrics provider — and the whole player stack
 * behind it — into every consumer of this one. The view layer, which
 * already composes both, does the composing (`useFeatureOverrideInput`).
 */
type TFeatureOverrideRootState = { featureOverride: FeatureOverrideState };

const INITIAL_STATE: FeatureOverrideState = {
  isVisible: false,
  rows: [],
  focusedIndex: 0
};

/**
 * Reads both persisted stores into one row per registered feature. The
 * effective stage is resolved by the model's pure resolver — the same one
 * `FeatureActivation` uses — so the screen can never drift from the engine.
 */
function buildFeatureOverrideRows(): Array<TFeatureOverrideRow> {
  const overrides: Record<string, boolean> = readFeatureOverrides();
  const stageOverrides: Record<string, FeatureStage> =
    readFeatureStageOverrides();

  return registeredFeatures.map((feature): TFeatureOverrideRow => {
    const hasOverride: boolean = Object.prototype.hasOwnProperty.call(
      overrides,
      feature
    );
    const hasStageOverride: boolean = Object.prototype.hasOwnProperty.call(
      stageOverrides,
      feature
    );
    const registryStage: FeatureStage = featureRegistry[feature].stage;
    const stageOverride: FeatureStage | null = hasStageOverride
      ? stageOverrides[feature]
      : null;

    return {
      feature,
      override: hasOverride ? overrides[feature] : null,
      registryStage,
      stageOverride,
      effectiveStage: resolveEffectiveStage(registryStage, stageOverride)
    };
  });
}

/** Cycles an activation value: DEFAULT(null) → ON(true) → OFF(false) → DEFAULT. */
function nextOverrideValue(
  current: boolean | null,
  direction: TFeatureOverrideCycleDirection
): boolean | null {
  const order: Array<boolean | null> = [null, true, false];
  const currentIndex: number = order.findIndex(
    (value: boolean | null): boolean => value === current
  );
  const delta: number = direction === "next" ? 1 : -1;
  const nextIndex: number =
    (currentIndex + delta + order.length) % order.length;
  return order[nextIndex];
}

/**
 * Cycles a stage value: DEFAULT(null) → DISABLED → IN_DEVELOPMENT → IN_TESTING
 * → DEFAULT. `RELEASED` is intentionally not offered — see
 * `OVERRIDABLE_FEATURE_STAGES`.
 */
function nextStageValue(
  current: FeatureStage | null,
  direction: TFeatureOverrideCycleDirection
): FeatureStage | null {
  const order: Array<FeatureStage | null> = [
    null,
    ...OVERRIDABLE_FEATURE_STAGES
  ];
  const currentIndex: number = order.findIndex(
    (value: FeatureStage | null): boolean => value === current
  );
  const delta: number = direction === "next" ? 1 : -1;
  const nextIndex: number =
    (currentIndex + delta + order.length) % order.length;
  return order[nextIndex];
}

/** Persists one row's activation override (null = DEFAULT removes the key). */
function persistOverride(
  row: TFeatureOverrideRow,
  value: boolean | null
): void {
  if (value === null) {
    clearFeatureOverride(row.feature);
  } else {
    setFeatureOverride(row.feature, value);
  }
}

/** Persists one row's stage override (null = DEFAULT removes the key). */
function persistStageOverride(
  row: TFeatureOverrideRow,
  stage: FeatureStage | null
): void {
  if (stage === null) {
    clearFeatureStageOverride(row.feature);
  } else {
    setFeatureStageOverride(row.feature, stage);
  }
}

/**
 * Opens the screen, seeding rows from both persisted stores. A no-op in release
 * builds: the trigger is not installed and this guard rejects the action.
 */
export const openFeatureOverride = createAsyncThunk<
  void,
  void,
  { state: TFeatureOverrideRootState }
>("featureOverride/open", async (_: void, { dispatch }) => {
  if (!isDevToolsBuild()) {
    return;
  }
  dispatch(showFeatureOverride(buildFeatureOverrideRows()));
});

/**
 * Advances the focused control's value in the given direction and persists it
 * (DEFAULT removes the key). Which value moves depends on what has focus: a
 * Stage control cycles stages, an Activation control cycles ON/OFF. Ignored on
 * the reset controls.
 */
export const cycleFeatureOverride = createAsyncThunk<
  void,
  TFeatureOverrideCycleDirection,
  { state: TFeatureOverrideRootState }
>(
  "featureOverride/cycle",
  async (direction: TFeatureOverrideCycleDirection, { dispatch, getState }) => {
    try {
      const { rows, focusedIndex } = getState().featureOverride;
      const control: TFeatureOverrideControl | null = resolveControl(
        focusedIndex,
        rows.length
      );
      // rowIndex < 0 covers both reset buttons and the Video Debug Overlay
      // switch: none of them is a per-feature value this thunk can cycle.
      if (control === null || control.rowIndex < 0) {
        return;
      }
      const row: TFeatureOverrideRow = rows[control.rowIndex];

      if (control.kind === FeatureOverrideControlKind.STAGE) {
        persistStageOverride(row, nextStageValue(row.stageOverride, direction));
      } else {
        persistOverride(row, nextOverrideValue(row.override, direction));
      }

      dispatch(setFeatureOverrideRows(buildFeatureOverrideRows()));
    } catch (error: unknown) {
      Log.e(TAG, "cycleFeatureOverride()", error);
    }
  }
);

/**
 * Clears every activation override and refreshes the rows to DEFAULT. Stage
 * overrides are deliberately left untouched.
 */
export const resetAllFeatureOverrides = createAsyncThunk<
  void,
  void,
  { state: TFeatureOverrideRootState }
>("featureOverride/resetAll", async (_: void, { dispatch }) => {
  try {
    clearAllFeatureOverrides();
    dispatch(setFeatureOverrideRows(buildFeatureOverrideRows()));
  } catch (error: unknown) {
    Log.e(TAG, "resetAllFeatureOverrides()", error);
  }
});

/**
 * Clears every stage override so all features resolve at their registry stage.
 * Activation overrides are deliberately left untouched.
 */
export const resetAllStageOverrides = createAsyncThunk<
  void,
  void,
  { state: TFeatureOverrideRootState }
>("featureOverride/resetAllStages", async (_: void, { dispatch }) => {
  try {
    clearAllFeatureStageOverrides();
    dispatch(setFeatureOverrideRows(buildFeatureOverrideRows()));
  } catch (error: unknown) {
    Log.e(TAG, "resetAllStageOverrides()", error);
  }
});

/** Sets a specific row's activation override to an explicit value — pointer path. */
export const setRowOverride = createAsyncThunk<
  void,
  { index: number; value: boolean | null },
  { state: TFeatureOverrideRootState }
>(
  "featureOverride/setRow",
  async (
    payload: { index: number; value: boolean | null },
    { dispatch, getState }
  ) => {
    try {
      const { rows } = getState().featureOverride;
      if (payload.index < 0 || payload.index >= rows.length) {
        return;
      }
      persistOverride(rows[payload.index], payload.value);
      dispatch(setFeatureOverrideRows(buildFeatureOverrideRows()));
    } catch (error: unknown) {
      Log.e(TAG, "setRowOverride()", error);
    }
  }
);

/** Sets a specific row's stage override to an explicit value — pointer path. */
export const setRowStageOverride = createAsyncThunk<
  void,
  { index: number; stage: FeatureStage | null },
  { state: TFeatureOverrideRootState }
>(
  "featureOverride/setRowStage",
  async (
    payload: { index: number; stage: FeatureStage | null },
    { dispatch, getState }
  ) => {
    try {
      const { rows } = getState().featureOverride;
      if (payload.index < 0 || payload.index >= rows.length) {
        return;
      }
      persistStageOverride(rows[payload.index], payload.stage);
      dispatch(setFeatureOverrideRows(buildFeatureOverrideRows()));
    } catch (error: unknown) {
      Log.e(TAG, "setRowStageOverride()", error);
    }
  }
);

const featureOverrideSlice = createSlice({
  name: "featureOverride",
  initialState: INITIAL_STATE,
  reducers: {
    /** Shows the screen with the supplied rows. */
    showFeatureOverride(
      state,
      action: PayloadAction<Array<TFeatureOverrideRow>>
    ): void {
      state.isVisible = true;
      state.rows = action.payload;
      state.focusedIndex = 0;
    },

    /** Hides the screen. */
    closeFeatureOverride(state): void {
      state.isVisible = false;
      state.focusedIndex = 0;
    },

    /** Replaces the rows after a persistence change. */
    setFeatureOverrideRows(
      state,
      action: PayloadAction<Array<TFeatureOverrideRow>>
    ): void {
      state.rows = action.payload;
    },

    /** Moves focus down, stopping at the last reset control. */
    focusNextControl(state): void {
      if (state.focusedIndex < getControlCount(state.rows.length) - 1) {
        state.focusedIndex += 1;
      }
    },

    /** Moves focus up, stopping at the first control. */
    focusPrevControl(state): void {
      if (state.focusedIndex > 0) {
        state.focusedIndex -= 1;
      }
    },

    /** Sets focus directly — used by pointer hover/focus. */
    setFocusedControl(state, action: PayloadAction<number>): void {
      const index: number = action.payload;
      if (index >= 0 && index < getControlCount(state.rows.length)) {
        state.focusedIndex = index;
      }
    }
  }
});

export const {
  showFeatureOverride,
  closeFeatureOverride,
  setFeatureOverrideRows,
  focusNextControl,
  focusPrevControl,
  setFocusedControl
} = featureOverrideSlice.actions;

export default featureOverrideSlice.reducer;

/* ── Selectors ────────────────────────────────────────────────────────── */

export const selectIsFeatureOverrideVisible = (
  state: TFeatureOverrideRootState
): boolean => state.featureOverride.isVisible;

export const selectFeatureOverrideRows = (
  state: TFeatureOverrideRootState
): Array<TFeatureOverrideRow> => state.featureOverride.rows;

export const selectFeatureOverrideFocusedIndex = (
  state: TFeatureOverrideRootState
): number => state.featureOverride.focusedIndex;

/** Index of the "Reset All Stage Overrides" control. */
export const selectResetStageOverridesIndex = (
  state: TFeatureOverrideRootState
): number => getResetStageIndex(state.featureOverride.rows.length);

/** Index of the "Reset All Feature Overrides" control. */
export const selectResetFeatureOverridesIndex = (
  state: TFeatureOverrideRootState
): number => getResetActivationIndex(state.featureOverride.rows.length);

/** Index of the "Video Debug Overlay" switch. */
export const selectVideoDebugOverlayControlIndex = (
  state: TFeatureOverrideRootState
): number => getVideoDebugOverlayIndex(state.featureOverride.rows.length);

/** What the focused index currently addresses, or null when out of range. */
export const selectFocusedControl = (
  state: TFeatureOverrideRootState
): TFeatureOverrideControl | null =>
  resolveControl(
    state.featureOverride.focusedIndex,
    state.featureOverride.rows.length
  );
